import {
  ArrowLeftIcon,
  ArrowRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import React, { useEffect, useRef, useState } from 'react'
import { Button } from '../ui'

/**
 * A step-by-step walkthrough demonstrating all features of the Forkast app
 */
const DemoWalkthrough = ({ onClose }) => {
  const [step, setStep] = useState(1)
  const totalSteps = 7
  const modalRef = useRef(null)
  const [highlightHelpButton, setHighlightHelpButton] = useState(false)
  const touchStartX = useRef(null)
  const touchEndX = useRef(null)

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        handleClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleClose = () => {
    // If on the last step, highlight the help button before closing
    if (step === totalSteps) {
      setHighlightHelpButton(true)
      // Close immediately, but the highlight will happen after modal is gone
      onClose()
    } else {
      onClose()
    }
  }

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1)
    } else {
      handleClose()
    }
  }

  const handlePrev = () => {
    if (step > 1) {
      setStep(step - 1)
    }
  }

  const handleGoToStep = (newStep) => {
    setStep(newStep)
  }

  // Touch handlers for swipe gestures
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX
  }

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return

    const diff = touchStartX.current - touchEndX.current
    const threshold = 50 // Minimum distance required for a swipe

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swiped left -> go to next
        handleNext()
      } else {
        // Swiped right -> go to previous
        handlePrev()
      }
    }

    // Reset values
    touchStartX.current = null
    touchEndX.current = null
  }

  const getStepContent = () => {
    switch (step) {
      case 1:
        return {
          title: 'Welcome to Forkast! 🍽️',
          content:
            "Forkast helps you organize recipes, plan meals, and create shopping lists. Let's explore all the features together!",
        }
      case 2:
        return {
          title: 'Recipe Library 📚',
          content:
            'Browse and search through your collection of recipes. Use filters to find recipes by ingredient, cooking time, or rating.',
        }
      case 3:
        return {
          title: 'Create New Recipes 👨‍🍳',
          content:
            'Add your own recipes with ingredients, instructions, cooking time, and photos. Your recipes are stored securely in the cloud.',
        }
      case 4:
        return {
          title: 'Meal Planning Calendar 📅',
          content:
            'Organize your weekly meals by adding recipes to your meal calendar. Plan breakfast, lunch, and dinner for the entire week.',
        }
      case 5:
        return {
          title: 'Generate Shopping Lists 🛒',
          content:
            'Select recipes and automatically generate a shopping list with all needed ingredients, organized by category.',
        }
      case 6:
        return {
          title: 'Favorites & Ratings ❤️',
          content:
            'Heart your favorite recipes and rate them with forks to keep track of what you love. Filter your view to see only favorites.',
        }
      case 7:
        return {
          title: 'All Set! 🎉',
          content:
            "You're ready to use Forkast! If you need to see this tutorial again, just click the Help button in the header. Happy cooking!",
        }
      default:
        return {
          title: 'Welcome to Forkast! 🍽️',
          content:
            "Let's get started with a quick tour of all the features. 🚀",
        }
    }
  }

  // Add an effect to highlight the help button when walkthrough is closing
  useEffect(() => {
    if (highlightHelpButton) {
      // Wait for the modal to fully close before highlighting the button
      const timeoutId = setTimeout(() => {
        const helpButton = document.querySelector(
          'button[title="Show App Tutorial"]'
        )
        if (helpButton) {
          // Create a more obvious pulse effect
          const pulseEffect = () => {
            // Add green color
            helpButton.classList.add('text-green-600')

            // Add scale transform and transition
            helpButton.style.transform = 'scale(1.3)'
            helpButton.style.transition = 'transform 0.3s ease, color 0.3s ease'

            setTimeout(() => {
              // Reset to normal
              helpButton.style.transform = 'scale(1)'

              setTimeout(() => {
                // Second pulse
                helpButton.style.transform = 'scale(1.3)'

                setTimeout(() => {
                  // Return to normal
                  helpButton.style.transform = 'scale(1)'

                  setTimeout(() => {
                    // Clean up all changes
                    helpButton.classList.remove('text-green-600')
                    helpButton.style.transform = ''
                    helpButton.style.transition = ''
                  }, 300)
                }, 300)
              }, 300)
            }, 300)
          }

          // Run the pulse effect
          pulseEffect()
        }
      }, 300) // Delay to ensure modal is fully closed

      return () => clearTimeout(timeoutId)
    }
  }, [highlightHelpButton])

  const { title, content } = getStepContent()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div
        ref={modalRef}
        className="relative w-full max-w-md p-6 mx-auto bg-white rounded-lg shadow-xl"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        {/* Progress indicators */}
        <div className="flex justify-center mb-4">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <button
              key={i}
              className={`mx-1 h-2 w-2 rounded-full ${
                i + 1 === step ? 'bg-green-500' : 'bg-gray-300'
              }`}
              onClick={() => handleGoToStep(i + 1)}
              aria-label={`Go to step ${i + 1}`}
            />
          ))}
        </div>

        {/* Content - fixed height */}
        <div className="mb-6 min-h-[140px]">
          <h2 className="text-xl font-bold mb-2 text-gray-800">{title}</h2>
          <p className="text-gray-600">{content}</p>
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between">
          {step > 1 ? (
            <Button
              onClick={handlePrev}
              variant="text"
              size="sm"
              className="flex items-center"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
            </Button>
          ) : (
            <div className="w-24"></div> /* Empty placeholder to maintain layout */
          )}

          <Button
            onClick={handleNext}
            variant="primary"
            size="sm"
            className="flex items-center"
          >
            {step === totalSteps ? 'Finish' : 'Next'}
            {step !== totalSteps && <ArrowRightIcon className="h-4 w-4 ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default DemoWalkthrough
